/**
 * Services: the device's Windows services, read live, with start / stop / restart / pause / resume,
 * one Configure panel (startup type, recovery actions, log-on account), and the same action across
 * several devices.
 *
 * A live round trip to the machine, so it loads on this tab's first reveal rather than at page
 * open. Windows-only - the shell hides the tab on a Synology.
 *
 * The agent refuses a stop that would take running dependents down unless asked to, so the
 * confirmation here names them and is the place that consent is given. A service the agent will
 * not touch (its own, or one Windows cannot run without) arrives `locked` and shows only Start.
 *
 * Configure and Other devices are ONE panel each, shared by every row and opened by the row's
 * button - the users tab's rule: a row is rebuilt whenever the list is re-read, which would discard
 * a password or a ticked device list typed inside it.
 */
import type { Control } from 'webwin';
import * as auth from '../../api/auth.js';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import {
  ApprovalStatus,
  ServiceAction,
  ServiceFailureAction,
  ServiceState,
  type BulkServiceOutcome,
  type DeviceDto,
  type DeviceServiceDto,
  type ServiceStartupType
} from '../../contracts.gen.js';
import { join, plural } from '../../format.js';
import { isWindowsCaption } from '../../hardware.js';
import {
  BUILT_IN_ACCOUNTS,
  describeRecovery,
  describeSeconds,
  failureActionLabel,
  filterServices,
  isAutomatic,
  isBuiltInAccount,
  isRunning,
  isStoppedAutomatic,
  SERVICE_FILTERS,
  serviceStateLabel,
  SETTABLE_FAILURE_ACTIONS,
  SETTABLE_STARTUP_TYPES,
  startupTypeLabel
} from '../../services.js';
import { CardList } from '../../ui/CardList.js';
import { chip, type ChipTone } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { BoundedScroller } from '../../ui/Scroller.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, checkBox, comboBox, field, fieldRow, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, passwordBox, splitRow, stackOf, text, textBox, value, vstack } from '../../ui/layout.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

/** Rendered rows. Above it the filter is the way to find one, and the overflow line says so. */
const MAX_ROWS = 250;

/** The control actions offered for several devices at once. */
const BULK_ACTIONS: readonly { label: string; action: ServiceAction }[] = [
  { label: 'Start', action: ServiceAction.Start },
  { label: 'Stop', action: ServiceAction.Stop },
  { label: 'Restart', action: ServiceAction.Restart },
  { label: 'Pause', action: ServiceAction.Pause },
  { label: 'Resume', action: ServiceAction.Resume }
];

/** The log-on choices: the three built-in identities, then "this account". */
const ACCOUNT_CHOICES = [...BUILT_IN_ACCOUNTS.map(b => b.label), 'This account'];

export interface ServicesTab extends DeviceTab {
  applyPresence(): void;
}

export function servicesTab(dc: DeviceTabContext): ServicesTab {
  let busy = false;
  let loaded = false;
  let all: DeviceServiceDto[] = [];
  let machine = '';

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const status = new StatusLine();

  // ---- list ----
  const rows = new CardList<DeviceServiceDto>({
    key: s => s.name.toLowerCase(),
    version: s => JSON.stringify(s),
    build: s => buildRow(s),
    max: MAX_ROWS,
    spacing: GAP.sm
  });
  const scroller = new BoundedScroller(520);
  scroller.content.controls.add(rows.panel);

  const searchBox = textBox();
  searchBox.on('textchanged', () => apply());
  const filterPicker = comboBox(SERVICE_FILTERS, {
    itemText: f => f.label,
    selectedIndex: 0,
    onSelectionChanged: () => apply()
  });
  const counts = muted('');
  const waiting = muted('The list is read from the device when this tab opens, so it needs to be online.');
  const attentionNote = text('', { role: 'muted', font: FONT.small, wrap: true });
  attentionNote.visible = false;
  const refreshButton = button('Refresh', () => void load());

  const listHeader = splitRow({ align: 'center' });
  fillWith(listHeader, text('Windows services', { font: FONT.bold }));
  listHeader.controls.add(stackOf(buttonRow(), refreshButton));

  // ---------------------------------------------------------------
  //  The Configure panel
  // ---------------------------------------------------------------

  let configFor: DeviceServiceDto | null = null;
  const configTitle = text('', { font: FONT.bold });

  // Startup type
  const startupPicker = comboBox(SETTABLE_STARTUP_TYPES, { itemText: t => startupTypeLabel(t), selectedIndex: 0 });
  const startupSave = button('Save startup type', () => void saveStartup(), true);
  const startupStatus = new StatusLine();

  // Recovery
  const recoveryPickers = [0, 1, 2].map(() =>
    comboBox(SETTABLE_FAILURE_ACTIONS, { itemText: a => failureActionLabel(a), selectedIndex: 0 })
  );
  const restartDelayBox = textBox();
  const resetBox = textBox();
  const recoveryNote = text('', { role: 'muted', font: FONT.small, wrap: true });
  const recoverySave = button('Save recovery', () => void saveRecovery(), true);
  const recoveryStatus = new StatusLine();

  // Log-on account
  const accountPicker = comboBox(ACCOUNT_CHOICES, { itemText: c => c, selectedIndex: 0, onSelectionChanged: () => applyAccountChoice() });
  const accountBox = textBox();
  const accountPassword = passwordBox();
  const accountConfirm = passwordBox();
  const accountUserFields = stackOf(
    vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
    field('Account (.\\name for a local account, DOMAIN\\name, or DOMAIN\\name$ for a managed service account)', accountBox),
    fieldRow(field('Password', accountPassword), field('Confirm password', accountConfirm))
  );
  const accountSave = button('Save log-on account', () => void saveAccount(), true);
  const accountStatus = new StatusLine();
  const accountCurrent = text('', { role: 'muted', font: FONT.small, wrap: true });
  const accountAdminOnly = text(
    'Only an administrator can change the account a service logs on as - it decides which identity code runs under on this machine.',
    { role: 'muted', font: FONT.small, wrap: true }
  );
  const accountEditor = stackOf(
    vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
    field('Log on as', accountPicker),
    accountUserFields,
    stackOf(buttonRow(), accountSave),
    accountStatus.label,
    text(
      'Windows checks the password before anything changes, and the agent grants the account "Log on as a service" ' +
        'if it lacks it, as services.msc does. The password is recorded nowhere.',
      { role: 'muted', font: FONT.tiny, wrap: true }
    )
  );

  const configPanel = card({ tint: 'alt' });
  configPanel.controls.add(
    stackOf(
      vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false }),
      configTitle,
      stackOf(vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
        text('Startup type', { font: FONT.bold }),
        startupPicker,
        stackOf(buttonRow(), startupSave),
        startupStatus.label,
        text('Takes effect at the next start - the service keeps running or stays stopped as it is now.',
          { role: 'muted', font: FONT.tiny, wrap: true })),
      stackOf(vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
        text('Recovery', { font: FONT.bold }),
        fieldRow(field('First failure', recoveryPickers[0]!), field('Second failure', recoveryPickers[1]!)),
        fieldRow(field('Subsequent failures', recoveryPickers[2]!), field('Restart the service after (minutes)', restartDelayBox)),
        field('Reset the failure count after (minutes; 1440 is a day)', resetBox),
        recoveryNote,
        stackOf(buttonRow(), recoverySave),
        recoveryStatus.label),
      stackOf(vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
        text('Log on as', { font: FONT.bold }),
        accountCurrent,
        accountAdminOnly,
        accountEditor),
      stackOf(buttonRow(), button('Close', () => openConfig(null)))
    )
  );
  configPanel.visible = false;

  // ---------------------------------------------------------------
  //  The Other devices panel
  // ---------------------------------------------------------------

  let bulkFor: DeviceServiceDto | null = null;
  let bulkDevices: DeviceDto[] = [];
  const bulkChecks = new Map<number, ReturnType<typeof checkBox>>();
  const bulkTitle = text('', { font: FONT.bold });
  const bulkAction = comboBox(BULK_ACTIONS, { itemText: a => a.label, selectedIndex: 2 });
  const bulkStopDependents = checkBox('If a device has running services that depend on it, stop (and for a restart, start again) those too');
  const bulkList = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
  const bulkRun = button('Run', () => void runBulk(), true);
  const bulkStatus = new StatusLine();
  const bulkResults = new CardList<BulkServiceOutcome>({
    key: o => o.deviceId,
    version: o => JSON.stringify(o),
    build: o => buildOutcome(o),
    spacing: GAP.xs
  });
  const bulkPanel = card({ tint: 'alt' });
  bulkPanel.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      bulkTitle,
      field('Action', bulkAction),
      bulkStopDependents,
      text('Devices', { font: FONT.bold }),
      stackOf(buttonRow(), button('Select all online', () => selectBulk(true)), button('Clear', () => selectBulk(false))),
      bulkList,
      stackOf(buttonRow(), bulkRun, button('Close', () => openBulk(null))),
      bulkStatus.label,
      bulkResults.panel,
      text(
        "Each device is asked separately and answers for itself; every one gets its own row in its history. A device " +
          'whose agent predates this feature, or that is offline or archived, is reported rather than skipped silently.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );
  bulkPanel.visible = false;

  const body = card();
  body.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      listHeader,
      attentionNote,
      fieldRow(field('Show', filterPicker), field('Filter by name or description', searchBox)),
      configPanel,
      bulkPanel,
      status.label,
      waiting,
      counts,
      scroller
    )
  );

  panel.controls.addRange([h2('Services'), body]);

  // ---------------------------------------------------------------
  //  Rows
  // ---------------------------------------------------------------

  function stateTone(s: DeviceServiceDto): ChipTone {
    if (s.attention) return 'danger';
    switch (s.state) {
      case ServiceState.Running:
        return 'ok';
      case ServiceState.Stopped:
        return isStoppedAutomatic(s) ? 'warn' : 'neutral';
      case ServiceState.Paused:
        return 'warn';
      default:
        return 'info';
    }
  }

  function buildRow(s: DeviceServiceDto): Control {
    const heading = inlineRow();
    heading.controls.add(value(s.displayName, { font: FONT.bold }));
    heading.controls.add(chip(serviceStateLabel(s.state), stateTone(s)));
    heading.controls.add(chip(startupTypeLabel(s.startupType)));
    if (s.attention) heading.controls.add(chip('Needs attention', 'danger'));
    if (s.locked) heading.controls.add(chip('Protected', 'info'));

    const details = join(
      [
        s.name,
        s.account ? 'Runs as ' + s.account : '',
        s.processId ? 'PID ' + s.processId : '',
        s.runningDependents.length ? `${plural(s.runningDependents.length, 'running dependent')}` : '',
        s.recovery ? describeRecovery(s.recovery) : ''
      ],
      '  ·  '
    );

    const left = stackOf(
      vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
      heading,
      text(details, { role: 'muted', font: FONT.small, wrap: true })
    );
    if (s.attention) left.controls.add(text(s.attention, { role: 'danger', font: FONT.small, wrap: true }));
    if (s.description) left.controls.add(text(s.description, { role: 'muted', font: FONT.tiny, wrap: true }));
    if (s.lockedReason) left.controls.add(text(s.lockedReason, { role: 'muted', font: FONT.tiny, wrap: true }));

    // Buttons follow the state the row was built from; the list is re-read after every action, so
    // they never outlive it. Presence is re-checked by the handlers.
    const actions = buttonRow();
    const canStopRestart = isRunning(s) && s.acceptsStop && !s.locked;
    if (s.state === ServiceState.Paused) actions.controls.add(button('Resume', () => void control(s, ServiceAction.Resume)));
    else if (!isRunning(s)) actions.controls.add(button('Start', () => void control(s, ServiceAction.Start)));
    if (canStopRestart) actions.controls.add(button('Stop', () => void control(s, ServiceAction.Stop)));
    if (canStopRestart) actions.controls.add(button('Restart', () => void control(s, ServiceAction.Restart)));
    if (s.state === ServiceState.Running && s.acceptsPause && !s.locked)
      actions.controls.add(button('Pause', () => void control(s, ServiceAction.Pause)));
    if (!s.locked) actions.controls.add(button('Configure', () => openConfig(s)));
    if (!s.locked) actions.controls.add(button('Other devices…', () => void openBulk(s)));

    const row = splitRow({ spacing: GAP.md, align: 'start' });
    fillWith(row, left);
    row.controls.add(actions);
    return row;
  }

  function apply(): void {
    const filter = filterPicker.selectedItem ?? SERVICE_FILTERS[0]!;
    const shown = filterServices(all, filter, searchBox.text);
    rows.set(shown);
    scroller.fit();
    counts.text = !loaded || all.length === 0
      ? ''
      : rows.overflow > 0
        ? `Showing ${MAX_ROWS} of ${shown.length} matching - type in the filter to narrow the list.`
        : `${shown.length} of ${plural(all.length, 'service')} on ${machine}.`;
    counts.visible = counts.text.length > 0;

    const needing = all.filter(s => s.attention).length;
    attentionNote.text = needing
      ? `${plural(needing, 'automatic service')} stopped with an error or never started. Pick "Needs attention" to see ${needing === 1 ? 'it' : 'them'}.`
      : '';
    attentionNote.visible = needing > 0;
    dc.relayout();
  }

  // ---------------------------------------------------------------
  //  Loading
  // ---------------------------------------------------------------

  async function load(): Promise<void> {
    if (busy) return;
    busy = true;
    loaded = true;
    applyPresence();
    status.busy('Reading the services…');
    waiting.visible = false;
    dc.relayout();

    try {
      const result = await crm.listDeviceServices(dc.deviceId, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      if (!result.success) {
        all = [];
        status.error(result.error ?? 'Could not read the services.');
        return;
      }
      all = result.services;
      machine = result.machineName;
      status.clear();
      // A panel open on a service keeps describing the fresh copy, not the one it opened with.
      if (configFor) configFor = all.find(s => s.name === configFor!.name) ?? configFor;
    } catch (error) {
      if (!dc.ctx.cancelled()) {
        all = [];
        status.error(describeError(error));
      }
    } finally {
      busy = false;
      if (!dc.ctx.cancelled()) apply();
      applyPresence();
      dc.relayout();
    }
  }

  // ---------------------------------------------------------------
  //  Start / stop / restart / pause / resume
  // ---------------------------------------------------------------

  async function control(s: DeviceServiceDto, action: ServiceAction): Promise<void> {
    if (busy) return;
    if (!dc.isOnline()) {
      status.error('The device is offline.');
      dc.relayout();
      return;
    }

    const host = dc.device()?.name ?? 'this device';
    const takesDependents = action === ServiceAction.Stop || action === ServiceAction.Restart;
    const dependents = takesDependents && s.runningDependents.length
      ? '\n\nThese services depend on it and will be stopped first' +
        (action === ServiceAction.Restart ? ', then started again' : '') +
        ':\n' + s.runningDependents.join(', ')
      : '';
    if (action === ServiceAction.Stop || action === ServiceAction.Restart || action === ServiceAction.Pause) {
      const word = action === ServiceAction.Stop ? 'Stop' : action === ServiceAction.Restart ? 'Restart' : 'Pause';
      const ok = await confirm(dc.ctx.desktop, {
        title: `${word} service`,
        message:
          `${word} ${s.displayName} on ${host}?` +
          (action === ServiceAction.Stop && isAutomatic(s)
            ? '\n\nIt is set to start automatically, so it will start again when the machine restarts.'
            : '') +
          (action === ServiceAction.Pause ? '\n\nIt stays loaded but stops doing its work until it is resumed.' : '') +
          dependents
      });
      if (!ok || dc.ctx.cancelled()) return;
    }

    const verbs: Record<ServiceAction, [string, string, string]> = {
      [ServiceAction.Start]: ['Starting', 'Started', 'running'],
      [ServiceAction.Stop]: ['Stopping', 'Stopped', 'stopped'],
      [ServiceAction.Restart]: ['Restarting', 'Restarted', 'running'],
      [ServiceAction.Pause]: ['Pausing', 'Paused', 'paused'],
      [ServiceAction.Resume]: ['Resuming', 'Resumed', 'running']
    };
    const [doing, done, already] = verbs[action];
    let outcome: string | null = null;
    let warning: string | null = null;
    busy = true;
    applyPresence();
    status.busy(`${doing} ${s.displayName}… this can take up to half a minute.`);
    dc.relayout();

    try {
      const result = await crm.controlDeviceService(
        dc.deviceId,
        { name: s.name, action, stopDependents: takesDependents && s.runningDependents.length > 0 },
        dc.ctx.dispose.signal()
      );
      if (dc.ctx.cancelled()) return;
      if (!result.success) {
        status.error(result.error ?? 'The device refused.');
        return;
      }
      outcome = result.changed ? `${done} ${s.displayName}.` : `${s.displayName} was already ${already}.`;
      warning = result.warning;
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }

    if (outcome !== null && !dc.ctx.cancelled()) {
      await load();
      if (warning) status.error(`${outcome} ${warning}`);
      else status.ok(outcome);
      dc.relayout();
    }
  }

  // ---------------------------------------------------------------
  //  Configure
  // ---------------------------------------------------------------

  function isAdmin(): boolean {
    return (auth.currentUser()?.role ?? '').toLowerCase() === 'admin';
  }

  function openConfig(s: DeviceServiceDto | null): void {
    if (s !== null && !dc.isOnline()) {
      status.error('The device is offline.');
      dc.relayout();
      return;
    }
    configFor = s;
    configPanel.visible = s !== null;
    startupStatus.clear();
    recoveryStatus.clear();
    accountStatus.clear();
    accountPassword.text = '';
    accountConfirm.text = '';
    if (s !== null) {
      openBulk(null);
      configTitle.text = `Configure ${s.displayName}`;

      const index = SETTABLE_STARTUP_TYPES.indexOf(s.startupType);
      startupPicker.selectedIndex = index >= 0 ? index : 2;

      const r = s.recovery;
      const actions = r ? [r.firstFailure, r.secondFailure, r.subsequentFailures] : [ServiceFailureAction.None, ServiceFailureAction.None, ServiceFailureAction.None];
      actions.forEach((a, i) => {
        recoveryPickers[i]!.selectedIndex = a === ServiceFailureAction.Restart ? 1 : 0;
      });
      restartDelayBox.text = r ? String(Math.round((r.restartDelaySeconds / 60) * 100) / 100) : '1';
      resetBox.text = r && r.resetAfterSeconds >= 0 ? String(Math.round((r.resetAfterSeconds / 60) * 100) / 100) : '1440';
      const other = actions.filter(a => a === ServiceFailureAction.Reboot || a === ServiceFailureAction.RunCommand);
      recoveryNote.text = join(
        [
          !r ? 'The device could not read the current recovery actions.' : '',
          r && r.resetAfterSeconds < 0 ? 'The failure count is currently set never to reset.' : '',
          other.length
            ? `Currently set outside the CRM to "${other.map(failureActionLabel).join('" / "')}"` +
              (r?.command ? ` (${r.command})` : '') +
              '. Saving here replaces that with the choices above - the CRM never sets those.'
            : '',
          r ? `Now: ${describeRecovery(r).replace('On failure: ', '')}; reset after ${describeSeconds(r.resetAfterSeconds)}.` : ''
        ],
        ' '
      );
      recoveryNote.visible = recoveryNote.text.length > 0;

      accountCurrent.text = `Currently logs on as ${s.account ?? 'an unknown account'}.`;
      const builtIn = BUILT_IN_ACCOUNTS.findIndex(b => b.account.toLowerCase() === (s.account ?? '').toLowerCase());
      accountPicker.selectedIndex = builtIn >= 0 ? builtIn : ACCOUNT_CHOICES.length - 1;
      accountBox.text = builtIn >= 0 ? '' : s.account ?? '';
      accountEditor.visible = isAdmin();
      accountAdminOnly.visible = !isAdmin();
      applyAccountChoice();
    }
    dc.relayout();
  }

  function applyAccountChoice(): void {
    accountUserFields.visible = accountPicker.selectedIndex === ACCOUNT_CHOICES.length - 1;
    dc.relayout();
  }

  async function saveStartup(): Promise<void> {
    if (busy || configFor === null) return;
    const s = configFor;
    const wanted = startupPicker.selectedItem as ServiceStartupType | undefined;
    if (wanted === undefined) return;

    if (wanted === SETTABLE_STARTUP_TYPES[3]) {
      const ok = await confirm(dc.ctx.desktop, {
        title: 'Disable service',
        message:
          `Disable ${s.displayName} on ${dc.device()?.name ?? 'this device'}?\n\nIt will not start again - not at ` +
          'boot, and not when anything asks for it - until its startup type is changed back.' +
          (isRunning(s) ? ' It keeps running until it is stopped or the machine restarts.' : '')
      });
      if (!ok || dc.ctx.cancelled()) return;
    }

    await saving(startupStatus, async () => {
      const result = await crm.setDeviceServiceStartupType(dc.deviceId, { name: s.name, startupType: wanted }, dc.ctx.dispose.signal());
      if (!result.success) return { error: result.error ?? 'The device refused.' };
      return {
        done: result.changed ? `Set ${s.displayName} to ${startupTypeLabel(wanted)}.` : `${s.displayName} was already ${startupTypeLabel(wanted)}.`
      };
    });
  }

  async function saveRecovery(): Promise<void> {
    if (busy || configFor === null) return;
    const s = configFor;
    const delay = Number(restartDelayBox.text.trim());
    const reset = Number(resetBox.text.trim());
    if (!Number.isFinite(delay) || delay < 0 || delay > 1440) {
      recoveryStatus.error('The restart delay is a number of minutes, from 0 to 1440.');
      dc.relayout();
      return;
    }
    if (!Number.isFinite(reset) || reset < 0 || reset > 525600) {
      recoveryStatus.error('The reset period is a number of minutes, from 0 to 525,600 (a year).');
      dc.relayout();
      return;
    }
    const [first, second, subsequent] = recoveryPickers.map(p => (p.selectedItem as ServiceFailureAction | undefined) ?? ServiceFailureAction.None) as [
      ServiceFailureAction,
      ServiceFailureAction,
      ServiceFailureAction
    ];

    await saving(recoveryStatus, async () => {
      const result = await crm.setDeviceServiceRecovery(
        dc.deviceId,
        {
          name: s.name,
          firstFailure: first,
          secondFailure: second,
          subsequentFailures: subsequent,
          resetAfterSeconds: Math.round(reset * 60),
          restartDelaySeconds: Math.round(delay * 60)
        },
        dc.ctx.dispose.signal()
      );
      if (!result.success) return { error: result.error ?? 'The device refused.' };
      return { done: result.changed ? `Saved the recovery actions of ${s.displayName}.` : `The recovery actions of ${s.displayName} were already set that way.` };
    });
  }

  async function saveAccount(): Promise<void> {
    if (busy || configFor === null || !isAdmin()) return;
    const s = configFor;
    const choice = accountPicker.selectedIndex;
    const custom = choice === ACCOUNT_CHOICES.length - 1;
    const account = custom ? accountBox.text.trim() : BUILT_IN_ACCOUNTS[choice]!.account;
    const managed = account.endsWith('$');

    if (custom) {
      if (!account) {
        accountStatus.error('Enter the account.');
        dc.relayout();
        return;
      }
      if (isBuiltInAccount(account)) {
        accountStatus.error('Pick the built-in account from the list instead.');
        dc.relayout();
        return;
      }
      if (!managed && !accountPassword.text) {
        accountStatus.error("Enter the account's password.");
        dc.relayout();
        return;
      }
      // Checked here because only here can it be: the server never sees the second copy.
      if (accountPassword.text !== accountConfirm.text) {
        accountStatus.error('The two passwords do not match.');
        dc.relayout();
        return;
      }
    }

    const ok = await confirm(dc.ctx.desktop, {
      title: 'Change log-on account',
      message:
        `Set ${s.displayName} on ${dc.device()?.name ?? 'this device'} to log on as ${account}?` +
        (isRunning(s) ? '\n\nIt keeps running as its current account until it is restarted.' : '') +
        (custom && !managed ? '\n\nThe account is granted "Log on as a service" if it does not already have it.' : '')
    });
    if (!ok || dc.ctx.cancelled()) return;

    await saving(accountStatus, async () => {
      const result = await crm.setDeviceServiceAccount(
        dc.deviceId,
        { name: s.name, account, password: custom && !managed ? accountPassword.text : null },
        dc.ctx.dispose.signal()
      );
      if (!result.success) return { error: result.error ?? 'The device refused.' };
      accountPassword.text = '';
      accountConfirm.text = '';
      return {
        done: join(
          [
            result.changed ? `${s.displayName} now logs on as ${account}.` : `${s.displayName} already logs on as ${account}.`,
            result.grantedLogonRight ? `${account} was granted "Log on as a service".` : '',
            result.restartRequired ? 'Restart the service for it to take effect.' : ''
          ],
          ' '
        )
      };
    });
  }

  /** The save shape the three Configure sections share: busy, status, re-read, report. */
  async function saving(line: StatusLine, work: () => Promise<{ done?: string; error?: string }>): Promise<void> {
    if (!dc.isOnline()) {
      line.error('The device is offline.');
      dc.relayout();
      return;
    }
    let done: string | null = null;
    busy = true;
    applyPresence();
    line.busy('Saving…');
    dc.relayout();
    try {
      const outcome = await work();
      if (dc.ctx.cancelled()) return;
      if (outcome.error) line.error(outcome.error);
      else done = outcome.done ?? 'Saved.';
    } catch (error) {
      if (!dc.ctx.cancelled()) line.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }

    if (done !== null && !dc.ctx.cancelled()) {
      await load();
      if (configFor) openConfig(configFor);
      line.ok(done);
      dc.relayout();
    }
  }

  // ---------------------------------------------------------------
  //  Other devices
  // ---------------------------------------------------------------

  async function openBulk(s: DeviceServiceDto | null): Promise<void> {
    bulkFor = s;
    bulkPanel.visible = s !== null;
    bulkStatus.clear();
    bulkResults.set([]);
    if (s === null) {
      dc.relayout();
      return;
    }
    openConfig(null);
    bulkTitle.text = `${s.displayName} (${s.name}) on several devices`;
    bulkList.controls.clear();
    bulkChecks.clear();
    bulkStatus.busy("Reading this customer's devices…");
    dc.relayout();

    try {
      const customerId = dc.device()?.customerId;
      const devices = await crm.getDevices(customerId, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled() || bulkFor !== s) return;
      bulkDevices = devices
        .filter(d => d.status === ApprovalStatus.Approved && !d.archived && isWindowsCaption(d.os?.caption))
        .sort((a, b) => a.name.localeCompare(b.name));
      for (const d of bulkDevices) {
        const online = d.presence?.isOnline ?? false;
        const box = checkBox(`${d.name}${online ? '' : ' (offline)'}`, d.id === dc.deviceId);
        bulkChecks.set(d.id, box);
        bulkList.controls.add(box);
      }
      bulkStatus.set(`${plural(bulkDevices.length, 'Windows device')} for this customer.`);
    } catch (error) {
      if (!dc.ctx.cancelled()) bulkStatus.error(describeError(error));
    }
    dc.relayout();
  }

  function selectBulk(on: boolean): void {
    for (const d of bulkDevices) {
      const box = bulkChecks.get(d.id);
      if (box) box.checked = on && (d.presence?.isOnline ?? false);
    }
  }

  async function runBulk(): Promise<void> {
    if (busy || bulkFor === null) return;
    const s = bulkFor;
    const action = (bulkAction.selectedItem ?? BULK_ACTIONS[2]!).action;
    const label = (bulkAction.selectedItem ?? BULK_ACTIONS[2]!).label;
    const ids = bulkDevices.filter(d => bulkChecks.get(d.id)?.checked).map(d => d.id);
    if (ids.length === 0) {
      bulkStatus.error('Tick at least one device.');
      dc.relayout();
      return;
    }

    const ok = await confirm(dc.ctx.desktop, {
      title: `${label} on ${plural(ids.length, 'device')}`,
      message:
        `${label} ${s.displayName} (${s.name}) on ${plural(ids.length, 'device')}?` +
        ((action === ServiceAction.Stop || action === ServiceAction.Restart) && bulkStopDependents.checked
          ? '\n\nRunning services that depend on it will be stopped too, on every device that has any.'
          : '') +
        ((action === ServiceAction.Stop || action === ServiceAction.Restart) && !bulkStopDependents.checked
          ? '\n\nA device where it has running dependents will refuse and say so.'
          : '')
    });
    if (!ok || dc.ctx.cancelled()) return;

    busy = true;
    applyPresence();
    bulkStatus.busy(`Asking ${plural(ids.length, 'device')}… each can take up to half a minute.`);
    bulkResults.set([]);
    dc.relayout();
    try {
      const result = await crm.controlServiceOnDevices(
        { deviceIds: ids, name: s.name, action, stopDependents: bulkStopDependents.checked },
        dc.ctx.dispose.signal()
      );
      if (dc.ctx.cancelled()) return;
      const succeeded = result.outcomes.filter(o => o.success).length;
      bulkResults.set(result.outcomes);
      if (succeeded === result.outcomes.length) bulkStatus.ok(`Done on all ${plural(succeeded, 'device')}.`);
      else bulkStatus.error(`Done on ${succeeded} of ${plural(result.outcomes.length, 'device')} - see below.`);
    } catch (error) {
      if (!dc.ctx.cancelled()) bulkStatus.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }

    // This device may have been one of them.
    if (!dc.ctx.cancelled() && ids.includes(dc.deviceId)) await load();
  }

  function buildOutcome(o: BulkServiceOutcome): Control {
    const row = inlineRow();
    row.controls.add(chip(o.success ? (o.changed ? 'Done' : 'No change') : 'Not done', o.success ? 'ok' : 'danger'));
    row.controls.add(value(o.deviceName, { font: FONT.bold }));
    const note = o.success ? o.warning : o.error;
    if (note) row.controls.add(text(note, { role: o.success ? 'muted' : 'danger', font: FONT.small, wrap: true }));
    return row;
  }

  function applyPresence(): void {
    const online = dc.isOnline();
    refreshButton.enabled = online && !busy;
    startupSave.enabled = online && !busy;
    recoverySave.enabled = online && !busy;
    accountSave.enabled = online && !busy;
    bulkRun.enabled = !busy;
  }

  applyPresence();

  return {
    panel,
    onShow() {
      if (loaded || busy || !dc.isOnline()) return;
      void load();
    },
    applyPresence
  };
}
